import React, { useState } from "react";
import { useQuery } from "react-query";
import "cropperjs/dist/cropper.css";
import "./Calender.css";
import axios from "axios";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline' // a plugin!
import moment from "moment";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import TimeConverter from "../helpers/timeFormatConverter";
import tickImage from "../../images/singleinvoice/tick-green.svg";
import crossImage from "../../images/singleinvoice/cross-red.svg";
import Delete from "../../images/icons/delete.svg";
import editImage from "../../images/icons/edit-pen.svg";
import UserIcon from "../../images/icons/single-user-icon.svg";
import Type from "../../images/icons/type.svg";
import birthIcon from "../../images/icons/dat-of-birth.svg";
import timer from "../../images/icons/timer.svg";
import downArrow from "../../images/icons/down-arrow.svg"
import { fetchUserProfiles, useUserProfiles } from "../User/userProfileQueries";

// Mock constants (replace with actual imports/values)
const SUPER_ADMIN = 1;
const ADMIN = 2;
const USER = 3;
const PROJECT_MANAGER = 6;
const TEAM_MANAGER = 7;

Modal.setAppElement("#root");

// Status color mapping
const STATUS_COLORS = {
    Pending: {
        backgroundColor: "#FDDFBB",    // Orange background
        color: "#E39C4F"              // Dark orange text
    },
    Approved: {
        backgroundColor: "#D5F3D8",    // Green background
        color: "#70AD69"              // Dark green text
    },
    Rejected: {
        backgroundColor: "#FFBBBD",    // Red background
        color: "#A74E4E"              // Dark red text
    },
    Cancelled: {
        backgroundColor: "#FFBBBD",    // Grey background
        color: "#A74E4E"              // Dark grey text
    },
    Birthday: {
        backgroundColor: "#3498db",    // Blue background
        color: "#FFFFFF"              // White text
    },
    Holiday: {
        backgroundColor: "#9b59b6",   // Purple background
        color: "#FFFFFF",            // White text
    },
};

const fetchLeaves = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.get("/api/time-off-request/view", {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch leave data");
    }
};

// const fetchHolidays = async (year, countryCode = "US") => {
//     try {
//         const response = await axios.get(
//             `https://date.nager.at/api/v3/publicholidays/${year}/${countryCode}`
//         );
//         return response.data;
//     } catch (error) {
//         console.error("Failed to fetch holidays:", error);
//         return [];
//     }
// };

const HOLIDAYS_2025 = [
    { date: "2025-01-01", name: "New Year's Day", description: "Global celebration of the new year" },
    { date: "2025-04-18", name: "Good Friday", description: "Christian holiday commemorating the crucifixion of Jesus" },
    { date: "2025-04-20", name: "Easter Sunday", description: "Christian holiday celebrating the resurrection of Jesus" },
    { date: "2025-05-01", name: "International Workers' Day", description: "Celebration of laborers and the working class" },
    { date: "2025-07-04", name: "Independence Day (US)", description: "US national holiday celebrating independence" },
    { date: "2025-10-02", name: "Gandhi Jayanti", description: "Indian holiday honoring Mahatma Gandhi's birthday" },
    { date: "2025-11-27", name: "Thanksgiving (US)", description: "US holiday for giving thanks and feasting" },
    { date: "2025-12-25", name: "Christmas Day", description: "Christian holiday celebrating the birth of Jesus" },
    { date: "2025-12-26", name: "Boxing Day", description: "Day for giving gifts to service workers" },
    // Add more holidays as needed
];

const groupEventsByKey = (events) => {
    const groupedEvents = {};

    events.forEach((event) => {
        const userName = event.title.split(' - ')[0] || event.title.split("'s")[0] || event.title || "Holiday";
        const eventType = event.title.includes("Birthday") ? "Birthday" : event.title.includes("Holiday") ? "Holiday" :
            event.title.split(' - ')[1] || "Leave";
        const groupKey = event.start ? `${event.start}-${event.end}-${userName}-${eventType}` : `${event.date}-${userName}-${eventType}`;

        if (!groupedEvents[groupKey]) {
            groupedEvents[groupKey] = [];
        }
        groupedEvents[groupKey].push(event);
    });

    return Object.keys(groupedEvents).map((key) => {
        const eventsInGroup = groupedEvents[key];
        const firstEvent = eventsInGroup[0];

        return {
            title: eventsInGroup.length > 1
                ? `${firstEvent.title} (+${eventsInGroup.length - 1})`
                : firstEvent.title,
            start: firstEvent.start || firstEvent.date,
            end: firstEvent.end,
            backgroundColor: firstEvent.backgroundColor,
            borderColor: firstEvent.backgroundColor,
            textColor: firstEvent.color,
            extendedProps: {
                allEvents: eventsInGroup,
                groupKey: key
            },
            classNames: ['calendar-event']
        };
    });
};

const Calender = () => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [clickedDate, setClickedDate] = useState(null);
    const [expandedEventIndex, setExpandedEventIndex] = useState(null);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const dummyImage = "/uploads/lowProfileImage/user_default.png";
    const { user } = useSelector((state) => state.auth);
    const token = useSelector((state) => state.auth.token);
    const { data: leaves, isLoading, error, refetch } = useQuery(
        ["leaves", token],
        () => fetchLeaves(token),
        { enabled: !!token }
    );

    const { data: userProfiles = [], refetch: refetchUserProfiles, isLoading: userProfileLoading, isError } = useUserProfiles(token);

    const currentYear = new Date().getFullYear();
    // const { data: holidays, isLoading: isHolidaysLoading, error: holidaysError, } = useQuery(
    //     ["holidays", currentYear],
    //     () => fetchHolidays(currentYear, "US"), // Default to US, can be made configurable
    //     {
    //         enabled: true,
    //     }
    // );

    const uniqueBirthdays = new Set();
    const uniqueHolidays = new Set();

    // const events = leaves ? leaves.map(leave => {
    //     const dob = new Date(leave.user.dob);
    //     const currentYear = new Date().getFullYear();
    //     const birthday = leave.user.dob ?
    //         new Date(Date.UTC(currentYear, dob.getMonth(), dob.getDate())) : null;
    //     const birthdayInIST = birthday ?
    //         new Date(birthday.toLocaleString("en-US", { timeZone: global.config.getTimeZone() }))
    //             .toISOString().split("T")[0] : null;

    //     const fullName = leave.user?.first_name
    //         ? `${leave.user.first_name} ${leave.user.middle_name && leave.user.middle_name !== 'null'
    //             ? leave.user.middle_name + ' '
    //             : ''}${leave.user.last_name || ''}`.trim()
    //         : leave.user?.company_first_name
    //             ? `${leave.user.company_first_name} ${leave.user.company_last_name || ''}`.trim()
    //             : `${leave.user?.company_name || ''} (Company)`;

    //     let birthdayEvent = null;
    //     if (birthdayInIST && !uniqueBirthdays.has(`${birthdayInIST}-${fullName}`)) {
    //         birthdayEvent = {
    //             title: `${fullName}'s Birthday 🎉`,
    //             date: birthdayInIST,
    //             backgroundColor: STATUS_COLORS.Birthday.backgroundColor,
    //             color: STATUS_COLORS.Birthday.color,
    //             profileImage: leave.user.profile_image ?
    //                 `/uploads/lowProfileImage/${leave.user.profile_image}` : dummyImage,
    //             description: "Celebrate this special day! 🎂🎁"
    //         };
    //         uniqueBirthdays.add(`${birthdayInIST}-${fullName}`);
    //     }

    //     const leaveEvents = [];
    //     const startDate = new Date(leave.start_date);
    //     const endDate = new Date(leave.end_date);
    //     const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    //     const issueDate = new Date(leave.created_at);

    //     // Create a single event spanning all days of the leave
    //     leaveEvents.push({
    //         title: `${fullName} - ${leave.type_of_leave.leave_type}${totalDays > 1 ? ` (${totalDays} days)` : ''}`,
    //         start: new Date(startDate.toLocaleString("en-US", {
    //             timeZone: global.config.getTimeZone()
    //         })).toISOString().split("T")[0],
    //         end: new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toISOString().split("T")[0], // Add 1 day to make end date inclusive
    //         backgroundColor: STATUS_COLORS[leave.status]?.backgroundColor || "#95a5a6",
    //         color: STATUS_COLORS[leave.status]?.color || "#FFFFFF",
    //         profileImage: leave.user.profile_image ?
    //             `/uploads/lowProfileImage/${leave.user.profile_image}` : dummyImage,
    //         description: leave.note,
    //         status: leave.status,
    //         user: fullName,
    //         typeOfLeave: leave.type_of_leave.leave_type,
    //         issueDate: issueDate,
    //         totalDays: totalDays,
    //         startDate: new Date(startDate).toLocaleDateString("en-US"),
    //         endDate: new Date(endDate).toLocaleDateString("en-US"),
    //         startTime: leave.start_time ? leave.start_time : "9",
    //         endTime: leave.end_time ? leave.end_time : "18",
    //         note: leave.note,
    //         leaveId: leave._id,
    //     });

    //     return [...(birthdayEvent ? [birthdayEvent] : []), ...leaveEvents];
    // }).flat() : [];

    const events = [];
    if (leaves) {
        leaves.forEach((leave) => {
            const fullName = leave.user?.first_name
                ? `${leave.user.first_name} ${leave.user.middle_name && leave.user.middle_name !== "null"
                    ? leave.user.middle_name + " "
                    : ""
                    }${leave.user.last_name || ""}`.trim()
                : leave.user?.company_first_name
                    ? `${leave.user.company_first_name} ${leave.user.company_last_name || ""
                        }`.trim()
                    : `${leave.user?.company_name || ""} (Company)`;

            // Leave event
            const startDate = new Date(leave.start_date);
            const endDate = new Date(leave.end_date);
            const totalDays =
                Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
            const issueDate = new Date(leave.created_at);

            events.push({
                title: `${fullName} - ${leave.type_of_leave.leave_type}${totalDays > 1 ? ` (${totalDays} days)` : ""
                    }`,
                start: new Date(
                    startDate.toLocaleString("en-US", {
                        timeZone: global.config.getTimeZone(),
                    })
                )
                    .toISOString()
                    .split("T")[0],
                end: new Date(new Date(endDate).setDate(endDate.getDate() + 1))
                    .toISOString()
                    .split("T")[0],
                backgroundColor:
                    STATUS_COLORS[leave.status]?.backgroundColor || "#95a5a6",
                color: STATUS_COLORS[leave.status]?.color || "#FFFFFF",
                profileImage: leave.user.profile_image
                    ? `/Uploads/lowProfileImage/${leave.user.profile_image}`
                    : dummyImage,
                description: leave.note,
                status: leave.status,
                user: fullName,
                typeOfLeave: leave.type_of_leave.leave_type,
                issueDate: issueDate,
                totalDays: totalDays,
                startDate: new Date(startDate).toLocaleDateString("en-US"),
                endDate: new Date(endDate).toLocaleDateString("en-US"),
                startTime: leave.start_time ? leave.start_time : "9",
                endTime: leave.end_time ? leave.end_time : "18",
                note: leave.note,
                leaveId: leave._id,
                eventType: "Leave",
            });
        });
    }

    // Process birthdays from user profiles
    if (userProfiles) {
        userProfiles.forEach((userProfile) => {
            const dob = new Date(userProfile.dob);
            const currentYear = new Date().getFullYear();
            const birthday = userProfile.dob
                ? new Date(Date.UTC(currentYear, dob.getMonth(), dob.getDate()))
                : null;
            const birthdayInIST = birthday
                ? new Date(
                    birthday.toLocaleString("en-US", {
                        timeZone: global.config.getTimeZone(),
                    })
                )
                    .toISOString()
                    .split("T")[0]
                : null;

            const fullName = userProfile.first_name
                ? `${userProfile.first_name} ${userProfile.middle_name && userProfile.middle_name !== "null"
                    ? userProfile.middle_name + " "
                    : ""
                    }${userProfile.last_name || ""}`.trim()
                : userProfile.company_first_name
                    ? `${userProfile.company_first_name} ${userProfile.company_last_name || ""
                        }`.trim()
                    : `${userProfile.company_name || ""} (Company)`;

            if (birthdayInIST && !uniqueBirthdays.has(`${birthdayInIST}-${fullName}`)) {
                events.push({
                    title: `${fullName}'s Birthday 🎉`,
                    date: birthdayInIST,
                    backgroundColor: STATUS_COLORS.Birthday.backgroundColor,
                    color: STATUS_COLORS.Birthday.color,
                    profileImage: userProfile.profile_image
                        ? `/Uploads/lowProfileImage/${userProfile.profile_image}`
                        : dummyImage,
                    description: "Celebrate this special day! 🎂🎁",
                    eventType: "Birthday",
                });
                uniqueBirthdays.add(`${birthdayInIST}-${fullName}`);
            }
        });
    }

    // Process static holidays
    HOLIDAYS_2025.forEach((holiday) => {
        const holidayDate = holiday.date;
        const holidayKey = `${holidayDate}-${holiday.name}`;
        if (!uniqueHolidays.has(holidayKey)) {
            events.push({
                title: `${holiday.name} (Holiday)`,
                date: holidayDate,
                backgroundColor: STATUS_COLORS.Holiday.backgroundColor,
                color: STATUS_COLORS.Holiday.color,
                profileImage: dummyImage,
                description: holiday.description || "Public Holiday",
                eventType: "Holiday",
            });
            uniqueHolidays.add(holidayKey);
        }
    });
    // if (holidays) {
    //     holidays.forEach((holiday) => {
    //         const holidayDate = holiday.date; // e.g., "2025-01-01"
    //         const holidayKey = `${holidayDate}-${holiday.name}`;
    //         if (!uniqueHolidays.has(holidayKey)) {
    //             events.push({
    //                 title: `${holiday.name} (Holiday)`,
    //                 date: holidayDate,
    //                 backgroundColor: STATUS_COLORS.Holiday.backgroundColor,
    //                 color: STATUS_COLORS.Holiday.color,
    //                 profileImage: dummyImage, // No user-specific image for holidays
    //                 description: holiday.localName || "Public Holiday",
    //                 eventType: "Holiday",
    //             });
    //             uniqueHolidays.add(holidayKey);
    //         }
    //     });
    // }

    const isHolidayDate = (date) => {
        const dateStr = moment(date).format("YYYY-MM-DD");
        return events.some(
            (event) =>
                event.eventType === "Holiday" &&
                (event.date === dateStr || event.start === dateStr)
        );
    };

    const handleEventClick = (clickInfo) => {
        setSelectedEvents(clickInfo.event.extendedProps.allEvents || []);
        // const eventDate = clickInfo.event.start || clickInfo.event.date;
        // setClickedDate(eventDate ? new Date(eventDate) : null);
        const eventDate = clickInfo.event.start || clickInfo.event.date;
        let clickedDate = null;
        let clickedCellDate = null;

        // Find the correct td using click coordinates
        const { jsEvent } = clickInfo;
        const cells = document.querySelectorAll('.fc-daygrid-day[data-date]');
        let clickedCell = null;

        // Match click coordinates to a cell's bounding rectangle
        for (const cell of cells) {
            const rect = cell.getBoundingClientRect();
            if (
                jsEvent.clientX >= rect.left &&
                jsEvent.clientX <= rect.right &&
                jsEvent.clientY >= rect.top &&
                jsEvent.clientY <= rect.bottom
            ) {
                clickedCell = cell;
                break;
            }
        }

        if (clickedCell && clickedCell.dataset.date) {
            clickedCellDate = clickedCell.dataset.date;
            clickedDate = new Date(clickedCellDate);
            // Adjust for timezone
            clickedDate = new Date(clickedDate.toLocaleString("en-US", { timeZone: global.config.getTimeZone() }));
        } else {
            // Fallback to event start
            clickedDate = new Date(clickInfo.event.start);
            console.warn("Could not find valid data-date attribute, falling back to event start:", clickedDate);
        }

        // Debug logging
        // console.log({
        //     clickedCellDate,
        //     clickedDate: clickedDate?.toString(),
        //     eventStart: clickInfo.event.start.toString(),
        //     eventEnd: clickInfo.event.end?.toString(),
        //     viewStart: clickInfo.view?.activeStart?.toString(),
        //     viewType: clickInfo.view?.type,
        //     row: clickInfo.el.fcSeg?.row,
        //     firstCol: clickInfo.el.fcSeg?.firstCol,
        //     allEvents: clickInfo.event.extendedProps.allEvents,
        //     clickedElement: clickInfo.el.outerHTML.substring(0, 200),
        //     clickedCellAttributes: clickedCell ? { ...clickedCell.dataset } : null,
        //     jsEventCoords: { clientX: jsEvent.clientX, clientY: jsEvent.clientY },
        //     matchedCellRect: clickedCell ? clickedCell.getBoundingClientRect() : null
        // });

        setClickedDate(clickedDate);
        setModalIsOpen(true);
        setExpandedEventIndex(null);
    };

    const handleDateClick = (dateClickInfo) => {
        const clicked = new Date(dateClickInfo.date);
        console.log("Clicked Date:", clicked);
        // setSelectedEvents([]);
        // setClickedDate(clicked);
        // setModalIsOpen(true);
        // setExpandedEventIndex(null);
    };

    const handleApprove = () => {
        if (expandedEventIndex !== null) {
            setIsApproveModalOpen(true);
        }
    };

    const handleConfirmApprove = async () => {
        if (expandedEventIndex === null) return;
        const leaveId = selectedEvents[expandedEventIndex].leaveId;
        try {
            setIsSubmitLoading(true);
            // Replace with actual mutation: await approveTimeOffRequestMutation.mutateAsync(selectedEvents[0].leaveId);
            // Mock API call
            // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
            // Replace with actual toast: toast.success("Time Off request approved successfully!", { ... });
            await axios.get(`/api/time-off-request/approve/${leaveId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            console.log("Time Off request approved successfully for ID:", selectedEvents[0].leaveId);
            // Replace with actual dispatch and state updates
            // dispatch(setNotification(Date.now()));
            // setSortedTor(prevState => prevState.map(item => item._id === selectedEvents[0].leaveId ? { ...item, status: "Approved" } : item));
            setModalIsOpen(false); // Close modal as placeholder for handleCloseDetailsModal
            refetch();
        } catch (error) {
            console.error("Approval error:", error);
            // Replace with actual toast: toast.error("Something went wrong in approval!", { ... });
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCancelApprove = () => {
        setIsApproveModalOpen(false);
    };

    const handleReject = () => {
        if (expandedEventIndex !== null) {
            setIsRejectModalOpen(true);
        }
    };

    const handleConfirmReject = async () => {
        if (expandedEventIndex === null) return;
        const leaveId = selectedEvents[expandedEventIndex].leaveId;
        try {
            setIsSubmitLoading(true);
            // Replace with actual mutation: await rejectTimeOffRequestMutation.mutateAsync(selectedEvents[0].leaveId);
            // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
            // Replace with actual toast: toast.success("Time-Off request rejected successfully!", { ... });
            await axios.get(`/api/time-off-request/reject/${leaveId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log("Time-Off request rejected successfully for ID:", leaveId);
            // Replace with actual dispatch and state updates
            // dispatch(setNotification(Date.now()));
            // setSortedTor(prevState => prevState.map(item => item._id === selectedEvents[0].leaveId ? { ...item, status: "Rejected" } : item));
            setModalIsOpen(false); // Close modal as placeholder for handleCloseDetailsModal
            refetch();
        } catch (error) {
            console.error("Rejection error:", error);
            // Replace with actual toast: toast.error("Something went wrong in TOR rejection!", { ... });
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCancelReject = () => {
        setIsRejectModalOpen(false);
    };

    const handleCancel = () => {
        if (expandedEventIndex !== null) {
            setIsCancelModalOpen(true);
        }
    };

    const handleConfirmCancel = async () => {
        if (expandedEventIndex === null) return;
        const leaveId = selectedEvents[expandedEventIndex].leaveId;
        try {
            setIsSubmitLoading(true);
            // Replace with actual mutation: await cancelTimeOffRequestMutation.mutateAsync(selectedEvents[0].leaveId);
            // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
            // Replace with actual toast: toast.success("Time-Off request cancelled successfully!", { ... });
            await axios.get(`/api/time-off-request/cancel/${leaveId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log("Time-Off request cancelled successfully for ID:", leaveId);
            // Replace with actual dispatch and state updates
            // dispatch(setNotification(Date.now()));
            // setSortedTor(prevState => prevState.map(item => item._id === selectedEvents[0].leaveId ? { ...item, status: "Cancelled" } : item));
            setModalIsOpen(false); // Close modal as placeholder for handleCloseDetailsModal
            refetch();
        } catch (error) {
            console.error("Cancel error:", error);
            // Replace with actual toast: toast.error("Something went wrong in TOR cancelation!", { ... });
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCancelCancel = () => {
        setIsCancelModalOpen(false);
    };

    const handleDelete = () => {
        if (expandedEventIndex !== null) {
            setIsDeleteModalOpen(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (expandedEventIndex === null) return;
        const leaveId = selectedEvents[expandedEventIndex].leaveId;
        try {
            setIsSubmitLoading(true);
            // Replace with actual mutation: await deleteTimeOffRequestMutation.mutateAsync(selectedEvents[0].leaveId);
            // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
            // Replace with actual toast: toast.success("TimeOff request removed successfully", { ... });
            await axios.get(`/api/time-off-request/delete/${leaveId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log("TimeOff request removed successfully for ID:", leaveId);
            // Replace with actual state update
            // setSortedTor(prevState => prevState.filter(tor => tor._id !== selectedEvents[0].leaveId));
            // dispatch(setNotification(Date.now()));
            setModalIsOpen(false); // Close modal as placeholder for handleCloseDetailsModal
            // Replace with actual refetch toggle: setIsRefetch(!isRefetch);
            refetch();
        } catch (error) {
            console.error("Delete error:", error);
            // Replace with actual toast: toast.error("Something went wrong", { ... });
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
    };

    if (isLoading) return <div className="loading">Loading calendar...</div>;
    if (error) return <div className="error">Error: {error.message}</div>;

    return (
        <div className="calendar-container">
            <FullCalendar
               // plugins={[dayGridPlugin]}
                plugins={[dayGridPlugin ]}
                initialView="dayGridMonth"
                events={groupEventsByKey(events)}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                height="auto"
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,dayGridWeek'
                }}
                buttonText={{
                    today: 'Today',
                    month: 'Month',
                    week: 'Week'
                }}
                dayCellClassNames={(arg) => {
                    return isHolidayDate(arg.date) ? ["holiday-date"] : [];
                }}
            />

            <Modal
                className="client-modal sprint-modal time-off-review time-off-review-new"
                isOpen={modalIsOpen}
                onRequestClose={() => setModalIsOpen(false)}
                style={{
                    content: {
                        height: "41%",
                        width: "30%",
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        parent: document.querySelector(".admin-outer.time.tracker"),
                    },
                }}
            >
                {selectedEvents.length > 0 && (

                    <div className="modal timeoff-review">
                        <div className="modal-inner-top-sec">
                            <div className="modal-close-invite outter-modal-main">
                                <a className="" onClick={() => setModalIsOpen(false)}>
                                    <img src={crossImage} alt="Close Icon" />{" "}
                                </a>
                            </div>
                            <div class="modal-client-head">
                                <h2 class="">
                                    Events on {clickedDate ? clickedDate.toLocaleDateString("en-US", {
                                        month: "long",
                                        day: "numeric",
                                        year: "numeric"
                                    }) : "Selected Date"}
                                </h2>
                            </div>
                        </div>

                        <div className="form-field-client-outer-main">
                            {selectedEvents.map((event, index) => (
                                <div
                                    key={index}
                                    className="event-card"
                                    style={{
                                        border: `4px solid ${event.backgroundColor}`,
                                        background: "#fff",
                                        marginBottom: "15px",
                                        borderRight: "0.5px solid #0000002b",
                                        borderBottom: "0.5px solid #0000002b",
                                        borderTop: "0.5px solid #0000002b",
                                        borderRadius: "8px",
                                        transition: "transform 0.2s",
                                        padding: "10px"
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px",
                                            // cursor: event.title.includes("Birthday") ? "default" : "pointer"
                                            cursor:
                                                event.eventType === "Birthday" || event.eventType === "Holiday"
                                                    ? "default"
                                                    : "pointer",
                                        }}
                                        // onClick={() => event.title.includes("Birthday") ? null :
                                        //     setExpandedEventIndex(expandedEventIndex === index ? null : index)}
                                        onClick={() =>
                                            event.eventType === "Birthday" || event.eventType === "Holiday"
                                                ? null
                                                : setExpandedEventIndex(
                                                    expandedEventIndex === index ? null : index
                                                )
                                        }
                                    >
                                        <img
                                            src={event.profileImage}
                                            alt="Profile"
                                            style={{
                                                width: "45px",
                                                height: "45px",
                                                borderRadius: "50%",
                                                border: `2px solid ${event.color}`,
                                                objectFit: "cover"
                                            }}
                                            onError={(e) => { e.target.src = dummyImage; }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <p style={{
                                                fontSize: "15px",
                                                margin: "0 0 5px 0",
                                                color: "#2c3e50",
                                                fontWeight: "500"
                                            }}>
                                                {event.title}
                                            </p>
                                            {event.status && (
                                                <span style={{
                                                    fontSize: "12px",
                                                    color: event.color,
                                                    background: event.backgroundColor,
                                                    padding: "2px 8px",
                                                    borderRadius: "10px",
                                                    display: "inline-block"
                                                }}>
                                                    {event.status}
                                                </span>
                                            )}
                                            {event.eventType === "Holiday" && (
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        color: event.color,
                                                        background: event.backgroundColor,
                                                        padding: "2px 8px",
                                                        borderRadius: "10px",
                                                        display: "inline-block",
                                                    }}
                                                >
                                                    Holiday
                                                </span>
                                            )}
                                        </div>
                                        {/* {!event.title.includes("Birthday") && (
                                            <span style={{
                                                color: "#7f8c8d",
                                                fontSize: "12px"
                                            }}>
                                                {expandedEventIndex === index ? (
                                                    <img className="arrow-down" src={downArrow} alt="Arrow" />
                                                ) : (
                                                    <img className="arrow-top" src={downArrow} alt="Arrow" />
                                                )}
                                            </span>
                                        )} */}
                                        {event.eventType === "Leave" && (
                                            <span
                                                style={{
                                                    color: "#7f8c8d",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                {expandedEventIndex === index ? (
                                                    <img className="arrow-down" src={downArrow} alt="Arrow" />
                                                ) : (
                                                    <img className="arrow-top" src={downArrow} alt="Arrow" />
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    {/* {!event.title.includes("Birthday") && expandedEventIndex === index && ( */}
                                    {event.eventType === "Leave" && expandedEventIndex === index && (
                                        <div>
                                            <div className="form-field-client-outer" style={{
                                                marginTop: "10px",
                                                padding: "8px",
                                                background: "#f8f9fa",
                                                borderRadius: "5px"
                                            }}>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={UserIcon} alt="usericon" />
                                                        </div>
                                                        User
                                                    </label>
                                                    <div className="timeoff-user-inner">
                                                        <span className="user-image-listingteam">
                                                            <img
                                                                src={event.profileImage}
                                                                alt={event.user}
                                                                className="member-image"
                                                                style={{ width: "30px", height: "30px", borderRadius: "50%" }}
                                                                onError={(e) => { e.target.src = dummyImage; }}
                                                            />
                                                        </span>
                                                        <span className="time-off-user-name">{event.user}</span>
                                                    </div>
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={Type} alt="Type" />
                                                        </div>
                                                        Type of Leave
                                                    </label>
                                                    {event.typeOfLeave}
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={birthIcon} alt="usericon" />
                                                        </div>
                                                        Issue Date
                                                    </label>
                                                    {event.issueDate && <>{moment.tz(event.issueDate, 'DD/MM/YYYY', global.config.getTimeZone()).format('DD/MM/YYYY')}</>}
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={Type} alt="usericon" />
                                                        </div>
                                                        Total Days
                                                    </label>
                                                    {event.totalDays} days
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={birthIcon} alt="usericon" />
                                                        </div>
                                                        Start Date
                                                    </label>
                                                    {event.startDate && <>{moment.tz(event.startDate, 'DD/MM/YYYY', global.config.getTimeZone()).format('DD/MM/YYYY')}</>}
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={birthIcon} alt="usericon" />
                                                        </div>
                                                        End Date
                                                    </label>
                                                    {event.endDate && <>{moment.tz(event.endDate, 'DD/MM/YYYY', global.config.getTimeZone()).format('DD/MM/YYYY')}</>}
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={timer} alt="usericon" />
                                                        </div>
                                                        Start Time
                                                    </label>
                                                    {event.startTime && <TimeConverter time={event.startTime} />}
                                                </div>
                                                <div className="form-field-client">
                                                    <label className="form-label">
                                                        <div className="icon-before-time">
                                                            <img src={timer} alt="usericon" />
                                                        </div>
                                                        End Time
                                                    </label>
                                                    {event.endTime && <TimeConverter time={event.endTime} />}
                                                </div>
                                                <div className="form-field-client remark">
                                                    <label className="form-label">Remarks</label>
                                                    <div className="remark-note">{event.note || "No remarks"}</div>
                                                </div>
                                            </div>
                                            <div className="form-field-client-btns form-field-client-btns-calendar timeoff-btn" style={{
                                                display: "flex",
                                                gap: "10px",
                                                marginTop: "15px",
                                                flexWrap: "wrap"
                                            }}>
                                                {(user && (user.userType === SUPER_ADMIN || user.userType === ADMIN || user.userType === TEAM_MANAGER)) && (event.status !== "Cancelled") && (
                                                    <>
                                                        {event.status === "Approved" ? (
                                                            <button className="action-btn modal-btn-calendar approved disable-cursor" disabled>
                                                                Approved
                                                                <img src={tickImage} alt="Approve" />
                                                            </button>
                                                        ) : (
                                                            <button className="action-btn modal-btn-calendar approve" onClick={handleApprove} disabled={isSubmitLoading}>
                                                                Approve
                                                                <img src={tickImage} alt="Approve" />
                                                            </button>
                                                        )}
                                                        <Modal
                                                            className="delete-modal"
                                                            isOpen={isApproveModalOpen}
                                                            onRequestClose={handleCancelApprove}
                                                            contentLabel="Confirm Approve"
                                                            style={{
                                                                content: {
                                                                    width: "300px",
                                                                    height: "200px",
                                                                    position: "fixed",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    transform: "translate(-17.5%, -50%)",
                                                                    borderRadius: "12px",
                                                                    background: "#fff",
                                                                    boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                                                                    border: "none"
                                                                },
                                                                overlay: {
                                                                    backgroundColor: "rgba(0,0,0,0.5)"
                                                                }
                                                            }}
                                                        >
                                                            <div className="delete-modal-content">
                                                                <h2>Confirm Approve</h2>
                                                                <p>Are you sure you want to Approve this Time Off Request?</p>
                                                                <div className="delete-modal-buttons">
                                                                    <button className="modal-btn modal-btn-calendar approve " onClick={handleConfirmApprove} disabled={isSubmitLoading}>
                                                                        Approve
                                                                        <img src={tickImage} alt="Confirm Approve" />
                                                                    </button>
                                                                    <button className="modal-btn modal-btn-calendar cancel" onClick={handleCancelApprove} disabled={isSubmitLoading}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </Modal>

                                                        {event.status === "Rejected" ? (
                                                            <button className="action-btn modal-btn-calendar rejected disable-cursor" disabled>
                                                                Rejected
                                                                <img src={crossImage} alt="Reject" />
                                                            </button>
                                                        ) : (
                                                            <button className="action-btn modal-btn-calendar reject" onClick={handleReject} disabled={isSubmitLoading}>
                                                                Reject
                                                                <img src={crossImage} alt="Reject" />
                                                            </button>
                                                        )}
                                                        <Modal
                                                            className="delete-modal"
                                                            isOpen={isRejectModalOpen}
                                                            onRequestClose={handleCancelReject}
                                                            contentLabel="Confirm Reject"
                                                            style={{
                                                                content: {
                                                                    width: "300px",
                                                                    height: "200px",
                                                                    position: "fixed",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    transform: "translate(-17.5%, -50%)",
                                                                    borderRadius: "12px",
                                                                    background: "#fff",
                                                                    boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                                                                    border: "none"
                                                                },
                                                                overlay: {
                                                                    backgroundColor: "rgba(0,0,0,0.5)"
                                                                }
                                                            }}
                                                        >
                                                            <div className="delete-modal-content">
                                                                <h2>Confirm Reject</h2>
                                                                <p>Are you sure you want to Reject this Time Off Request?</p>
                                                                <div className="delete-modal-buttons">
                                                                    <button className="modal-btn modal-btn-calendar reject" onClick={handleConfirmReject} disabled={isSubmitLoading}>
                                                                        Reject
                                                                        <img src={crossImage} alt="Reject" />
                                                                    </button>
                                                                    <button className="modal-btn modal-btn-calendar cancel" onClick={handleCancelReject} disabled={isSubmitLoading}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </Modal>
                                                    </>
                                                )}
                                                {((user && (user.userType === SUPER_ADMIN || user.userType === ADMIN || user.userType === TEAM_MANAGER)) ||
                                                    (event.startDate && moment(event.startDate, "MM/DD/YYYY").toDate() > new Date())) && (
                                                        <>
                                                            {event.status === "Cancelled" ? (
                                                                <button className="action-btn modal-btn-calendar rejected disable-cursor" disabled>
                                                                    cancelled
                                                                    <img src={crossImage} alt="Reject" />
                                                                </button>
                                                            ) : (
                                                                <button className="action-btn modal-btn-calendar reject" onClick={handleCancel} disabled={isSubmitLoading}>
                                                                    cancel
                                                                    <img src={crossImage} alt="Reject" />
                                                                </button>
                                                            )}
                                                            <Modal
                                                                className="delete-modal"
                                                                isOpen={isCancelModalOpen}
                                                                onRequestClose={handleCancelCancel}
                                                                contentLabel="Confirm Cancel"
                                                                style={{
                                                                    content: {
                                                                        width: "300px",
                                                                        height: "200px",
                                                                        position: "fixed",
                                                                        top: "50%",
                                                                        left: "50%",
                                                                        transform: "translate(-17.5%, -50%)",
                                                                        borderRadius: "12px",
                                                                        background: "#fff",
                                                                        boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                                                                        border: "none"
                                                                    },
                                                                    overlay: {
                                                                        backgroundColor: "rgba(0,0,0,0.5)"
                                                                    }
                                                                }}
                                                            >
                                                                <div className="delete-modal-content">
                                                                    <h2>Confirm Cancel</h2>
                                                                    <p>Are you sure you want to Cancel this Time Off Request?</p>
                                                                    <div className="delete-modal-buttons">
                                                                        <button className="modal-btn modal-btn-calendar reject" onClick={handleConfirmCancel} disabled={isSubmitLoading}>
                                                                            Cancel
                                                                            <img src={crossImage} alt="Reject" />
                                                                        </button>
                                                                        <button className="modal-btn modal-btn-calendar cancel" onClick={handleCancelCancel} disabled={isSubmitLoading}>
                                                                            Close
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </Modal>

                                                            <button
                                                                type="button"
                                                                className="action-btn modal-btn-calendar delete"
                                                                onClick={handleDelete}
                                                                disabled={isSubmitLoading}
                                                            >
                                                                <img src={Delete} alt="Delete" /> Delete Request
                                                            </button>
                                                            <Modal
                                                                className="delete-modal"
                                                                isOpen={isDeleteModalOpen}
                                                                onRequestClose={handleCancelDelete}
                                                                contentLabel="Confirm Delete"
                                                                style={{
                                                                    content: {
                                                                        width: "300px",
                                                                        height: "200px",
                                                                        position: "fixed",
                                                                        top: "50%",
                                                                        left: "50%",
                                                                        transform: "translate(-17.5%, -50%)",
                                                                        borderRadius: "12px",
                                                                        background: "#fff",
                                                                        boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                                                                        border: "none"
                                                                    },
                                                                    overlay: {
                                                                        backgroundColor: "rgba(0,0,0,0.5)"
                                                                    }
                                                                }}
                                                            >
                                                                <div className="delete-modal-content">
                                                                    <h2>Confirm Delete</h2>
                                                                    <p>Are you sure you want to delete this Time Off Request?</p>
                                                                    <div className="delete-modal-buttons">
                                                                        <button className="modal-btn modal-btn-calendar delete" onClick={handleConfirmDelete} disabled={isSubmitLoading}>
                                                                            Delete
                                                                            <img src={Delete} alt="Delete" />
                                                                        </button>
                                                                        <button className="modal-btn modal-btn-calendar cancel" onClick={handleCancelDelete} disabled={isSubmitLoading}>
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </Modal>
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                    {(event.eventType === "Birthday" || event.eventType === "Holiday") &&
                                        expandedEventIndex === index && (
                                            <div
                                                className="form-field-client-outer"
                                                style={{
                                                    marginTop: "10px",
                                                    padding: "8px",
                                                    background: "#f8f9fa",
                                                    borderRadius: "5px",
                                                }}
                                            >
                                                <div className="form-field-client remark">
                                                    <label className="form-label">Description</label>
                                                    <div className="remark-note">
                                                        {event.description || "No description"}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            ))}
                        </div>
                        {/* <button
                            onClick={() => setModalIsOpen(false)}
                            style={{
                                marginTop: "20px",
                                padding: "8px 20px",
                                background: "#3498db",
                                color: "#fff",
                                border: "none",
                                borderRadius: "20px",
                                cursor: "pointer",
                                fontWeight: "500",
                                transition: "background 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.background = "#2980b9"}
                            onMouseOut={(e) => e.target.style.background = "#3498db"}
                        >
                            Close
                        </button> */}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Calender;